import { Box, Stack } from '@mui/material'
import { useState, useEffect, useRef, useMemo } from 'react'
import {
  CalendarToday as CalendarTodayIcon,
  Map as MapIcon,
} from '@mui/icons-material'
import { addDays, addMonths, format, startOfMonth } from 'date-fns'
import { useDispatch, useSelector, batch } from 'react-redux'
import { localeDateFormat } from '../../../utils/localeUtils'
import DatePicker from '../../../shared/UI/DatePicker'
import Paragraph from '../../../shared/UI/Paragraph'
import SlideButton from './SlideButton'
import { RootStore } from '../../../redux/store'
import actionsService from '../../../services/actionsService'
import {
  setBookingFloorplanViewingDate,
  setBookingGridViewingDate,
  setDeskBookingFloorplanManuallySelected,
  setBookingSliderPosition,
  setDeskBookingFocussedZoneID,
  setDeskBookingDashboardResults,
  setDeskBookingLoading,
  setDeskBookingShowMeBookingID,
  setDeskBookingOpenFloorplan,
} from '../../../redux/reducers/deskBookingReducer'
import { BookingNavBarProps } from './types'
import HeadingTwo from '../../../shared/UI/HeadingTwo'
import OccupancyDisplay from './OccupancyDisplay'
import { dateToNumber } from '../utils/utils'
import { setBookingWizardStepState } from '../../../redux/reducers/deskBookingWizardReducer'
import { BookingWizardSteps } from '../BlockBooking/enums'
import {
  defaultFloorplanIdIfNoneSelected,
  defaultZoneIdIfNoneSelected,
  showComponents,
} from '../utils'
import { formatTime } from '../../../utils/date-utils'
import { BookingSliderIndex, BookingSearchType, Time } from './enums'
import {
  setDeskBookingSearchParams,
  setDeskBookingSearchResults,
} from '../../../redux/reducers/deskBookingSearchReducer'
import { FIRST_FLOORPLAN_IN_ARRAY } from '../consts'
import { showErrorMessage } from '../../../redux/reducers/snackbarReducer'
import BookingErrorMessage from '../utils/BookingErrorMessage'
import {BookingZone} from '../../../services/booking/types'
import {
  handleSearch,
} from '../bookingLogic'
import {checkIsOpenFloorplanArea} from './bookingFloorplanLogic'
import theme from '../../../theme/theme'
import { BookingHeaderColors } from './consts'

export function NavBar({ title, subTitle, onNav, occupancy }: BookingNavBarProps) {

  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => () => {

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    },
    []
  )

  const dispatch = useDispatch()

  const handleSearchError = (err: string) => {
    dispatch(showErrorMessage(<BookingErrorMessage name={err} />))
  }


  const { employeeDetails } = useSelector((state: RootStore) => state.appSettings)

  const [selectedZone] = useState<BookingZone>()

  const [selectedFromTo, setSelectedFromTo] = useState<{ from: Date | null; to: Date | null }>({
    from: null,
    to: null,
  })

  const {
    floorplanViewingDate,
    showGridView,
    showFloorplan,
    floorplans,
    featuresForFloorPlan,
    maxBookingDate,
    zones,
    focussedZoneID,
  } = useSelector((state: RootStore) => state.deskBooking)

  const { searchParams } = useSelector((state: RootStore) => state.deskBookingSearch)

  const userInfo = useSelector((state: RootStore) => state.userState.loggedInUser)

  const [searchType, setSearchType] = useState<BookingSearchType>(BookingSearchType.SINGLE)

  const employeeFloorplans = useMemo(
    () =>
      floorplans.filter(f => f.locationId === employeeDetails.location.id) ||
      floorplans[FIRST_FLOORPLAN_IN_ARRAY],
    [employeeDetails.location.id, floorplans]
  )

  const employeeZones = useMemo(
    () =>
      zones.filter(
        z =>
          employeeFloorplans.some(s => s.id === z.floorPlanId) &&
          z.belongsTo === employeeDetails.departmentId
      ),
    [employeeDetails.departmentId, employeeFloorplans, zones]
  )

  const selectedData = {
    selectedFromTo: {
      from: formatTime(selectedFromTo.from),
      to: formatTime(selectedFromTo.to)
    },
    searchType,
  }

  const minBookingDate = useMemo(() => addMonths(new Date(), -6), [])

  return (
    <Box display="flex" alignItems="flex-start" sx={{ width: '100%' }}>
      <Stack
        direction="column"
        sx={{
          flex: '1 1 0',
          minWidth: 0,
          pr: '24px',
          gap: '16px',
          '& .MuiTypography-root': { lineHeight: 1 },
        }}
      >
        <HeadingTwo title={subTitle ? `${title} - ${subTitle}` : title} fontWeight={500} />

        {occupancy?.zoneName && (
          <Paragraph
            size="13px"
            weight="medium"
            color={BookingHeaderColors.ZONE_TEXT}
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {occupancy.zoneName}
          </Paragraph>
        )}

        {occupancy && <OccupancyDisplay {...occupancy} />}
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        flexShrink={0}
        sx={{ gap: '24px' }}
      >
        {showFloorplan && (
          <Box display="flex" alignItems="center" sx={{ gap: '8px' }}>
            <Paragraph
              weight="medium"
              color={theme.palette.primary.main}
              style={{ whiteSpace: 'nowrap' }}
            >
              {dateToNumber(floorplanViewingDate) === dateToNumber(new Date())
                ? 'Today'
                : format(floorplanViewingDate, `EEE ${localeDateFormat}`)}
            </Paragraph>

            <DatePicker
              inputType="IconButton"
              maxDate={maxBookingDate}
              minDate={minBookingDate}
              label="Select date"
              value={floorplanViewingDate}
              onChange={newDate => {
                if (!newDate || Object.prototype.toString.call(newDate) !== '[object Date]') {
                  return
                }

                // Track analytics for calendar icon click
                actionsService.userAnalytics.postEvent(
                  'UIClickEvent',
                  'BookingFloorplanCalendarIconClick',
                  window.location.pathname,
                  window.location.hostname
                ).catch(error => console.error('Analytics tracking error:', error))

                if (newDate !== floorplanViewingDate) {
                  dispatch(setBookingFloorplanViewingDate(newDate))

                  if (!showFloorplan || !searchParams) {

                    return
                  }

                  dispatch(setDeskBookingDashboardResults([]))
                  dispatch(setDeskBookingLoading(true))

                  dispatch(
                    setDeskBookingSearchParams({
                      ...searchParams,
                      date: newDate,
                      autoSearch: true,
                    })
                  )

                  const zoneToPreserve = searchParams?.zoneId || focussedZoneID || 0
                  dispatch(setDeskBookingFocussedZoneID(zoneToPreserve))

                  handleSearch(
                    dispatch,
                    handleSearchError,
                    !showGridView,
                    zones,
                    featuresForFloorPlan,
                    employeeDetails,
                    userInfo,
                    {
                      selectedFromTo: {
                        from: Time.MIDNIGHT,
                        to: Time.MIDNIGHT_MINUS_ONE_SECOND
                      },
                      searchType,
                    },
                    searchParams?.locationId,
                    searchParams?.floorplanId,
                    null,
                    newDate,
                    addDays(newDate, 1)
                  )
                }
              }}
            />
          </Box>
        )}

        <SlideButton
          setIdx={showGridView ? BookingSliderIndex.GRID_VIEW : BookingSliderIndex.FLOORPLAN_VIEW}
          width="300px"
          buttons={[
            {
              label: 'Bookings',
              icon: <CalendarTodayIcon fontSize="inherit" sx={{ marginRight: '8px' }} />,
            },
            {
              label: 'Floor plan',
              icon: <MapIcon fontSize="inherit" sx={{ marginRight: '8px' }} />,
            },
          ]}
          onSelect={idx => batch(async () => {
            dispatch(setBookingFloorplanViewingDate(new Date()))
            if (idx === BookingSliderIndex.GRID_VIEW) {
              actionsService.userAnalytics.postEvent(
                'UIClickEvent',
                'BookingTabGridViewClick',
                window.location.pathname,
                window.location.hostname
              ).catch(error => console.error('Analytics tracking error:', error))
              dispatch(setBookingGridViewingDate(startOfMonth(new Date())))
              dispatch(setDeskBookingFloorplanManuallySelected(false))

              dispatch(setDeskBookingShowMeBookingID(undefined))

              showComponents(dispatch, { gridview: true, navbar: true, search: true })
              dispatch(setBookingSliderPosition({
                slidePosition: idx,
                buttonClick: true
              }))

              dispatch(setDeskBookingSearchResults([]))
              dispatch(setDeskBookingSearchParams(undefined))

            }
            if (idx === BookingSliderIndex.FLOORPLAN_VIEW) {
              actionsService.userAnalytics.postEvent(
                'UIClickEvent',
                'BookingTabFloorplanClick',
                window.location.pathname,
                window.location.hostname
              ).catch(error => console.error('Analytics tracking error:', error))
              dispatch(setBookingGridViewingDate(new Date()))
              showComponents(dispatch, { floorplan: true, navbar: true, search: false, filter: true })
              dispatch(setBookingWizardStepState(BookingWizardSteps.STEP_0_INACTIVE))
              dispatch(setDeskBookingFloorplanManuallySelected(true))
              dispatch(setBookingSliderPosition({
                slidePosition: idx,
                buttonClick: true
              }))

              if (!employeeZones.length && !employeeFloorplans.length) {
                return
              }

              const defaultFloorplanId = defaultFloorplanIdIfNoneSelected(
                employeeZones,
                employeeFloorplans
              )
              const isOpenFloorplan = checkIsOpenFloorplanArea(employeeDetails.location.id, defaultFloorplanId)
              dispatch(setDeskBookingOpenFloorplan(isOpenFloorplan))

              const zoneId: number = isOpenFloorplan ? 0 : (employeeZones[0] || defaultZoneIdIfNoneSelected(employeeZones)).id

              dispatch(setDeskBookingFocussedZoneID(isOpenFloorplan ? 0 : defaultZoneIdIfNoneSelected(employeeZones)))

              const date = new Date()
              dispatch(
                setDeskBookingSearchParams({
                  locationId: employeeDetails.location.id,
                  floorplanId: defaultFloorplanId,
                  zoneId,
                  date,
                  from: Time.MIDNIGHT,
                  to: Time.MIDNIGHT_MINUS_ONE_SECOND,
                  autoSearch: true,
                })
              )

              const singleQuery = true
              await handleSearch(
                dispatch,
                handleSearchError,
                singleQuery,
                zones,
                featuresForFloorPlan,
                employeeDetails,
                userInfo,
                {
                  selectedFromTo: {
                    from: Time.MIDNIGHT,
                    to: Time.MIDNIGHT_MINUS_ONE_SECOND,
                  },
                  searchType: BookingSearchType.SINGLE
                },
                employeeDetails.location.id,
                defaultFloorplanId,
                null,
                date,
                addDays(date, 1)
              )

            }
          })}
        />
      </Stack>
    </Box>
  )
}
